import React from "react";
import format from "date-fns/format";
import parse from "date-fns/parse";
import get from "lodash/get";
import Link from "next/link";
import { DefaultTable } from "../TableLayout";
import { formatPhone } from "~/utils/phone";
import Placeholder from "~/shared/Placeholder";

const Row = ({ row = {}, rowIndex = -1, children = [], renderVariant = null, renderStyle = null }) => {
    const childrenArr = Array.isArray(children) ? children : [children];

    const vari = renderVariant ? renderVariant(row) : "";
    const style = renderStyle ? renderStyle(row) : {};

    return (
        <tr className={vari} style={style}>
            {childrenArr.map((child, index) => {
                const elProps = child.props.field
                    ? { value: get(row, child.props.field), index: rowIndex }
                    : { data: row, index: rowIndex };

                const Component = React.cloneElement(child, elProps);

                const style = child?.props?.style ?? {};

                return (
                    <td key={index} style={typeof style === "function" ? style(row) : style}>
                        {Component}
                    </td>
                );
            })}
        </tr>
    );
};

const transformChildrenInArray = children => {
    return Array.isArray(children) ? children : [children];
};

const DataTable = ({
    records,
    loading,
    children,
    TableComponent = DefaultTable,
    setRowColor = undefined,
    isStick = false
}) => {
    const hasRow = !Array.isArray(children) && !!children?.props?.children;

    const childrenArr = hasRow ? transformChildrenInArray(children.props.children) : transformChildrenInArray(children);
    const headElements = childrenArr.map(el => el.props.label);

    return (
        <TableComponent style={{ width: "100%" }} isStick={isStick}>
            <thead>
            <tr>
                {headElements.map((title, index) => (
                    <th style={childrenArr?.[index]?.props?.titleStyle ?? {}} key={index}>
                        {typeof title === "function" ? title() : title}
                    </th>
                ))}
            </tr>
            </thead>
            <tbody>
            {loading ? (
                <tr>
                    {childrenArr.map((child, index) => (
                        <td key={index} style={child?.props?.style ?? {}}>
                            <Placeholder />
                        </td>
                    ))}
                </tr>
            ) : null}

            {!loading &&
                records.map((row, i) => {
                    let rowClass = "";
                    if (setRowColor) {
                        rowClass = setRowColor(row);
                    }
                    if (hasRow) {
                        const currentProps = { ...children.props, children: undefined, row };

                        return (
                            <Row key={i} {...currentProps} row={row} rowIndex={i} renderVariant={() => rowClass}>
                                {children.props.children}
                            </Row>
                        );
                    }

                    return (
                        <Row key={i} row={row} rowIndex={i} renderVariant={() => rowClass}>
                            {children}
                        </Row>
                    );
                })}
            </tbody>
        </TableComponent>
    );
};

const DateComponent = props => {
    const { value, from, to } = props;
    if (!value) {
        return <Text value="" />;
    }
    const toFormat = to ? to : "dd/MM/yyyy HH:mm";
    const date = value.includes("T") ? new Date(value) : parse(value, from, new Date());

    return <>{format(date, toFormat)}</>;
};

const DateFromISO8601 = props => {
    const toFormat = props.to ? props.to : "dd/MM/yyyy HH:mm";
    const fromFormat = "yyyy-MM-dd HH:mm:ss";

    if (!props.value) {
        return <Text value="" />;
    }

    const { value } = props;

    return <DateComponent value={value} to={toFormat} from={fromFormat} />;
};

const Text = props => {
    return props?.value ?? "";
};

const Phone = (props) => {
    if (!props.value) {
        return <Text value="" />;
    }

    let phoneFormatted = "";
    try {
        phoneFormatted = formatPhone(props.value);
    } catch (e) {
        phoneFormatted = props.value;
    }

    return <>{phoneFormatted}</>;
};

const Custom = props => {
    if (props.render && props.value) {
        return props.render(props.value, props.index);
    }

    return <Text value="" />;
};

const Computed = props => {
    if (props.render && props.data) {
        return props.render(props.data, props.index);
    }

    return <Text value="" />;
};

const LinkComponent = props => {
    const { external = false, link } = props;
    const linkStr = typeof link === "function" ? link(props?.value ?? "") : link;
    const label = props?.value ?? "";

    if (external) {
        return (
            <a href={linkStr} rel="noopener noreferrer" target="_blank">
                {label}
            </a>
        );
    }

    return (
        <Link href={linkStr}>
            <a>{label}</a>
        </Link>
    );
};

DataTable.Date = DateComponent;
DataTable.DateFromISO8601 = DateFromISO8601;
DataTable.Text = Text;
DataTable.Phone = Phone;
DataTable.Custom = Custom;
DataTable.Computed = Computed;
DataTable.Link = LinkComponent;
DataTable.Row = Row;

export default DataTable;
export { DateComponent, DateFromISO8601, Text, Custom };
